import React, { useState, useEffect, useCallback, useRef } from "react";
import Content from "../../../layout/content/Content";
import Head from "../../../layout/head/Head";
import {
  Block,
  BlockBetween,
  BlockDes,
  BlockHead,
  BlockHeadContent,
  BlockTitle,
  Button,
  Icon,
} from "../../../components/Component";
import { useNavigate, useParams, Link } from "react-router-dom";
import { useForm, FormProvider } from "react-hook-form";
import FormInput from "../../form-components/InputComponent";
import FormDatePicker from "../../form-components/DatePicker";
import { Col, Row, Modal, ModalBody, Card, Alert } from "reactstrap";
import { Table, Empty, Badge } from "antd";
import LoadingSpinner from "../../../components/loading/LoadingSpinner";
import moment from "moment";
import dayjs from "dayjs";
import Swal from "sweetalert2";
import superAdminService from "../../../api/super-admin/superAdminService";
import type {
  PricingDetail,
  LinkedCustomer,
} from "../../../types/super-admin";

const PricingDetails: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const formMethods = useForm({ mode: "onChange" });
  const { handleSubmit, setValue, reset, formState: { dirtyFields } } = formMethods;

  const [pricing, setPricing] = useState<PricingDetail | null>(null);
  const [linkedCustomers, setLinkedCustomers] = useState<LinkedCustomer[]>([]);
  const [loading, setLoading] = useState(true);
  const [editModal, setEditModal] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [alertInfo, setAlertInfo] = useState({
    type: "",
    message: "",
    errors: [] as string[],
  });

  const fetchPricingDetails = useCallback(async () => {
    try {
      setLoading(true);
      const res = await superAdminService.getPricingById(id);
      if (res.status && res.data) {
        setPricing(res.data);
      }
    } catch (error: any) {
      console.error("Failed to fetch pricing:", error.message);
    } finally {
      setLoading(false);
    }
  }, [id]);

  const fetchLinkedCustomers = useCallback(async () => {
    try {
      const res = await superAdminService.getPricingLinkedCustomers(id);
      if (res.status && res.data) {
        setLinkedCustomers(res.data);
      }
    } catch (error: any) {
      console.error("Failed to fetch linked customers:", error.message);
    }
  }, [id]);

  const hasFetched = useRef(false);

  useEffect(() => {
    if (hasFetched.current) return;
    hasFetched.current = true;
    fetchPricingDetails();
    fetchLinkedCustomers();
  }, [fetchPricingDetails, fetchLinkedCustomers]);

  const handleActivate = async () => {
    try {
      const res = await superAdminService.activatePricing(id);
      if (res.status) {
        Swal.fire("Activated!", "Pricing has been activated.", "success");
        fetchPricingDetails();
      } else {
        Swal.fire("Error", res.message || "Failed to activate", "error");
      }
    } catch (error: any) {
      Swal.fire("Error", error.message, "error");
    }
  };

  const handleDeactivate = async () => {
    Swal.fire({
      title: "Are you sure?",
      text: "This will deactivate the pricing plan.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, deactivate!",
      allowOutsideClick: false,
      buttonsStyling: false,
      customClass: {
        confirmButton: "btn btn-primary",
        cancelButton: "btn btn-secondary",
      },
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const res = await superAdminService.deactivatePricing(id);
          if (res.status) {
            Swal.fire(
              "Deactivated!",
              "Pricing has been deactivated.",
              "success"
            );
            fetchPricingDetails();
          } else {
            Swal.fire(
              "Error",
              res.message || "Failed to deactivate",
              "error"
            );
          }
        } catch (error: any) {
          Swal.fire("Error", error.message, "error");
        }
      }
    });
  };

  const openEditModal = () => {
    if (!pricing) return;
    setAlertInfo({ type: "", message: "", errors: [] });
    setValue("pricingName", pricing.pricingName || "");
    setValue("unitPrice", pricing.unitPrice || "");
    setValue(
      "validTo",
      pricing.validTo ? dayjs(pricing.validTo) : undefined
    );
    setEditModal(true);
  };

  const submitEdit = async (data: any) => {
    if (!pricing) return;
    setEditLoading(true);
    setAlertInfo({ type: "", message: "", errors: [] });

    try {
      const payload: any = {
        modulePriceId: pricing.modulePriceId,
      };

      if (dirtyFields.pricingName) payload.pricingName = data.pricingName?.trim();
      if (dirtyFields.unitPrice) payload.unitPrice = Number(data.unitPrice);
      if (dirtyFields.validTo) payload.validTo = new Date(data.validTo).toLocaleDateString("en-CA");

      if (Object.keys(payload).length === 1) {
        setAlertInfo({ type: "info", message: "No changes detected.", errors: [] });
        setEditLoading(false);
        return;
      }

      const res = await superAdminService.updatePricing(payload);
      if (res.status) {
        Swal.fire("Updated!", "Pricing has been updated.", "success");
        setEditModal(false);
        reset();
        fetchPricingDetails();
      } else {
        const errorMsg = Array.isArray(res.message)
          ? res.message.join(", ")
          : res.message || "An error occurred";
        setAlertInfo({ type: "error", message: errorMsg, errors: [] });
      }
    } catch (error: any) {
      setAlertInfo({
        type: "error",
        message: error.message || "An error occurred",
        errors: [],
      });
    } finally {
      setEditLoading(false);
    }
  };

  const customerColumns = [
    {
      title: "Customer Code",
      dataIndex: "customerCode",
      key: "customerCode",
      width: 150,
      ellipsis: true,
      render: (text: string, record: any) =>
        text ? (
          <Link
            to={`/sa-customers/${record.customerId}/control-panel`}
            className="link link-primary"
          >
            {text}
          </Link>
        ) : "-",
    },
    {
      title: "Customer Name",
      dataIndex: "customerName",
      key: "customerName",
      width: 240,
      ellipsis: true,
      render: (text: string, record: any) =>
        text ? (
          <Link
            to={`/sa-customers/${record.customerId}/control-panel`}
            className="link link-primary"
          >
            {text}
          </Link>
        ) : "-",
    },
    {
      title: "Valid From",
      dataIndex: "validFrom",
      key: "validFrom",
      width: 140,
      render: (date: string) => date ? moment(date).format("DD MMM YYYY") : "-",
    },
    {
      title: "Valid To",
      dataIndex: "validTo",
      key: "validTo",
      width: 140,
      render: (date: string) => date ? moment(date).format("DD MMM YYYY") : "-",
    },
    {
      title: "Status",
      dataIndex: "isActive",
      key: "isActive",
      width: 110,
      align: "center" as const,
      render: (isActive: boolean) => (
        <span
          className={`tb-status text-${isActive ? "success" : "danger"}`}
        >
          {isActive ? "Active" : "Inactive"}
        </span>
      ),
    },
  ];

  if (loading) {
    return (
      <React.Fragment>
        <Head title="SaaS Pricing Details" />
        <Content>
          <LoadingSpinner height="300px" />
        </Content>
      </React.Fragment>
    );
  }

  if (!pricing) {
    return (
      <React.Fragment>
        <Head title="SaaS Pricing Details" />
        <Content>
          <div className="text-center py-5">
            <Empty description="Pricing not found" />
            <Button
              color="primary"
              className="mt-3"
              onClick={() => navigate("/sa-pricing")}
            >
              Back to List
            </Button>
          </div>
        </Content>
      </React.Fragment>
    );
  }

  const pricingTypeColor: Record<string, string> = {
    Flat: "blue",
    "User-based": "cyan",
  };

  return (
    <React.Fragment>
      <Head title="SaaS Pricing Details" />
      <Content>
        {/* Header */}
        <BlockHead size="sm">
          <BlockBetween className="g-3">
            <BlockHeadContent>
              <BlockTitle page>
                {pricing.pricingName ||
                  `Pricing #${pricing.modulePriceId}`}{" "}
                <Badge
                  color={pricing.isActive ? "green" : "red"}
                >
                  {pricing.isActive ? "Active" : "Inactive"}
                </Badge>
              </BlockTitle>
              <BlockDes className="text-soft">
                <ul className="list-inline">
                  <li>
                    Application:{" "}
                    <span className="text-base">
                      {pricing.appName || "-"}
                    </span>
                  </li>
                  <li>
                    Type:{" "}
                    <Badge
                      color={
                        pricingTypeColor[pricing.pricingType] || "default"
                      }
                    >
                      {pricing.pricingType}
                    </Badge>
                  </li>
                </ul>
              </BlockDes>
            </BlockHeadContent>
            <BlockHeadContent>
              <ul className="nk-block-tools g-3">
                <li>
                  <Link to="/sa-pricing">
                    <Button
                      outline
                      className="bg-white d-none d-sm-inline-flex"
                    >
                      <Icon name="arrow-left"></Icon>
                      <span>Back</span>
                    </Button>
                  </Link>
                </li>
                <li>
                  <Button color="primary" onClick={openEditModal}>
                    <Icon name="edit-alt-fill"></Icon>
                    <span>Edit</span>
                  </Button>
                </li>
                <li>
                  {pricing.isActive ? (
                    <Button color="danger" onClick={handleDeactivate}>
                      <Icon name="na"></Icon>
                      <span>Deactivate</span>
                    </Button>
                  ) : (
                    <Button color="success" onClick={handleActivate}>
                      <Icon name="check-circle"></Icon>
                      <span>Activate</span>
                    </Button>
                  )}
                </li>
              </ul>
            </BlockHeadContent>
          </BlockBetween>
        </BlockHead>

        {/* Pricing Info */}
        <Block>
          <Row className="gy-5">
            <Col lg="6">
              <BlockHead>
                <BlockBetween>
                  <BlockHeadContent>
                    <BlockTitle tag="h5">Pricing Information</BlockTitle>
                  </BlockHeadContent>
                </BlockBetween>
              </BlockHead>
              <Card className="card-bordered">
                <ul className="data-list is-compact">
                  <li className="data-item">
                    <div className="data-col">
                      <div className="data-label">Pricing Name</div>
                      <div className="data-value">
                        {pricing.pricingName || "-"}
                      </div>
                    </div>
                  </li>
                  <li className="data-item">
                    <div className="data-col">
                      <div className="data-label">Application</div>
                      <div className="data-value">
                        {pricing.appName || "-"}
                      </div>
                    </div>
                  </li>
                  <li className="data-item">
                    <div className="data-col">
                      <div className="data-label">Currency</div>
                      <div className="data-value">
                        {pricing.currencyCode || "-"}
                      </div>
                    </div>
                  </li>
                  <li className="data-item">
                    <div className="data-col">
                      <div className="data-label">Pricing Type</div>
                      <div className="data-value">
                        <Badge
                          color={
                            pricingTypeColor[pricing.pricingType] || "default"
                          }
                        >
                          {pricing.pricingType}
                        </Badge>
                      </div>
                    </div>
                  </li>
                  <li className="data-item">
                    <div className="data-col">
                      <div className="data-label">Unit Price</div>
                      <div className="data-value">
                        {pricing.unitPrice != null
                          ? Number(pricing.unitPrice).toFixed(2)
                          : "-"}
                      </div>
                    </div>
                  </li>
                  <li className="data-item">
                    <div className="data-col">
                      <div className="data-label">Billing Frequency</div>
                      <div className="data-value">
                        {pricing.billingFrequency || "-"}
                      </div>
                    </div>
                  </li>
                </ul>
              </Card>
            </Col>
            <Col lg="6">
              <BlockHead>
                <BlockBetween>
                  <BlockHeadContent>
                    <BlockTitle tag="h5">Location & Validity</BlockTitle>
                  </BlockHeadContent>
                </BlockBetween>
              </BlockHead>
              <Card className="card-bordered">
                <ul className="data-list is-compact">
                  <li className="data-item">
                    <div className="data-col">
                      <div className="data-label">Country</div>
                      <div className="data-value">
                        {pricing.countryName || "Global"}
                      </div>
                    </div>
                  </li>
                  <li className="data-item">
                    <div className="data-col">
                      <div className="data-label">State</div>
                      <div className="data-value">
                        {pricing.stateName || "-"}
                      </div>
                    </div>
                  </li>
                  <li className="data-item">
                    <div className="data-col">
                      <div className="data-label">Valid From</div>
                      <div className="data-value">
                        {moment(pricing.validFrom).format("DD MMM YYYY")}
                      </div>
                    </div>
                  </li>
                  <li className="data-item">
                    <div className="data-col">
                      <div className="data-label">Valid To</div>
                      <div className="data-value">
                        {moment(pricing.validTo).format("DD MMM YYYY")}
                      </div>
                    </div>
                  </li>
                  <li className="data-item">
                    <div className="data-col">
                      <div className="data-label">Status</div>
                      <div className="data-value">
                        <span
                          className={`tb-status text-${
                            pricing.isActive ? "success" : "danger"
                          }`}
                        >
                          {pricing.isActive ? "Active" : "Inactive"}
                        </span>
                      </div>
                    </div>
                  </li>
                </ul>
              </Card>
            </Col>
          </Row>
        </Block>

        {/* Linked Customers Section */}
        <Block>
          <BlockHead size="sm">
            <BlockTitle tag="h5">
              Linked Customers ({linkedCustomers.length})
            </BlockTitle>
          </BlockHead>
          <Card className="card-bordered">
            <div className="card-inner">
              {linkedCustomers.length > 0 ? (
                <Table
                  columns={customerColumns}
                  dataSource={linkedCustomers}
                  rowKey="customerPriceId"
                  scroll={{ x: "max-content", y: 300 }}
                  pagination={{ pageSize: 10 }}
                  bordered
                  size="middle"
                />
              ) : (
                <Empty description="No customers linked to this pricing" />
              )}
            </div>
          </Card>
        </Block>
      </Content>

      {/* Edit Modal */}
      <Modal
        isOpen={editModal}
        toggle={() => setEditModal(false)}
        className="modal-dialog-centered"
        size="lg"
        backdrop="static"
      >
        <ModalBody>
          <a
            href="#cancel"
            onClick={(e) => {
              e.preventDefault();
              setEditModal(false);
              reset();
            }}
            className="close"
          >
            <Icon name="cross-sm"></Icon>
          </a>
          <div className="p-2">
            {alertInfo.message && (
              <div className="mb-3 mt-1">
                <Alert
                  color={alertInfo.type === "error" ? "danger" : "success"}
                  className="alert-icon alert-animated"
                >
                  <strong>{alertInfo.message}</strong>
                  <Icon
                    name={
                      alertInfo.type === "error"
                        ? "alert-circle"
                        : "check-circle"
                    }
                  />
                </Alert>
              </div>
            )}

            <h5 className="title">Edit Pricing</h5>
            <div className="mt-4">
              {/* Non-editable fields shown as read-only info */}
              <Row className="gy-2 mb-3">
                <Col sm="6">
                  <small className="text-soft d-block">Application</small>
                  <strong>{pricing.appName || "-"}</strong>
                </Col>
                <Col sm="6">
                  <small className="text-soft d-block">Currency</small>
                  <strong>{pricing.currencyCode || "-"}</strong>
                </Col>
                <Col sm="6">
                  <small className="text-soft d-block">Pricing Type</small>
                  <strong>{pricing.pricingType || "-"}</strong>
                </Col>
                <Col sm="6">
                  <small className="text-soft d-block">Billing Frequency</small>
                  <strong>{pricing.billingFrequency || "-"}</strong>
                </Col>
                <Col sm="6">
                  <small className="text-soft d-block">Country</small>
                  <strong>{pricing.countryName || "Global"}</strong>
                </Col>
                <Col sm="6">
                  <small className="text-soft d-block">State</small>
                  <strong>{pricing.stateName || "-"}</strong>
                </Col>
                <Col sm="6">
                  <small className="text-soft d-block">Valid From</small>
                  <strong>{moment(pricing.validFrom).format("DD MMM YYYY")}</strong>
                </Col>
              </Row>
              <hr className="mt-0 mb-3" />

              <FormProvider {...formMethods}>
                <form
                  className="content clearfix"
                  onSubmit={handleSubmit(submitEdit)}
                >
                  <Row className="gy-4">
                    <Col lg="12">
                      <FormInput
                        name="pricingName"
                        label="Pricing Name"
                        type="text"
                        required
                        maxLength={200}
                        placeholder="Enter pricing name"
                      />
                    </Col>
                    <Col lg="12">
                      <FormInput
                        name="unitPrice"
                        label="Unit Price"
                        type="number"
                        placeholder="Enter unit price"
                      />
                    </Col>
                    <Col lg="12">
                      <FormDatePicker
                        name="validTo"
                        label="Valid To"
                        required
                        placeholder="Select end date"
                        minDate={(() => {
                          const today = new Date();
                          today.setHours(0, 0, 0, 0);
                          const vf = pricing.validFrom
                            ? new Date(pricing.validFrom)
                            : today;
                          return vf > today ? vf : today;
                        })()}
                      />
                    </Col>
                  </Row>

                  <Row className="mt-4">
                    <Col lg="12">
                      <ul className="align-center flex-wrap flex-sm-nowrap gx-4 gy-2">
                        <li>
                          <Button color="primary" size="md" type="submit">
                            {editLoading ? (
                              <LoadingSpinner size="sm" inline />
                            ) : (
                              "Save Changes"
                            )}
                          </Button>
                        </li>
                        <li>
                          <a
                            href="#cancel"
                            onClick={(e) => {
                              e.preventDefault();
                              if (!editLoading) {
                                setEditModal(false);
                                reset();
                              }
                            }}
                            className={`link link-light${editLoading ? " disabled text-muted" : ""}`}
                            style={editLoading ? { pointerEvents: "none" } : {}}
                          >
                            Cancel
                          </a>
                        </li>
                      </ul>
                    </Col>
                  </Row>
                </form>
              </FormProvider>
            </div>
          </div>
        </ModalBody>
      </Modal>
    </React.Fragment>
  );
};

export default PricingDetails;
